import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Icon, IconName, Section, SectionHeading } from './ui';
import { CORRIDORS, PAYOUT_FLAGS } from './corridorList';
import { useJourneyPath } from './useJourneyPath';

const STEPS: { n: number; icon: IconName; titleKey: string; bodyKey: string }[] = [
  { n: 1, icon: 'globe', titleKey: 'sendSteps.step1.title', bodyKey: 'sendSteps.step1.body' },
  { n: 2, icon: 'user-check', titleKey: 'sendSteps.step2.title', bodyKey: 'sendSteps.step2.body' },
  { n: 3, icon: 'phone', titleKey: 'sendSteps.step3.title', bodyKey: 'sendSteps.step3.body' },
  { n: 4, icon: 'currency', titleKey: 'sendSteps.step4.title', bodyKey: 'sendSteps.step4.body' },
  { n: 5, icon: 'bank', titleKey: 'sendSteps.step5.title', bodyKey: 'sendSteps.step5.body' },
];

function columnClass(n: number): string {
  if (n === 4) return 'lg:col-span-2 lg:col-start-2';
  if (n === 5) return 'lg:col-span-2 lg:col-start-4';
  return 'lg:col-span-2';
}

// WU-HOW: a soft pointer travels from step 1 to step 5 while the section is on screen, pausing at each step.
// No line is drawn. Each card lights up as the pointer arrives and dims as it reaches the next one; step 5
// stays lit through the rest at the end of the loop (the approved D4 exception; CSS only, off under reduced
// motion). The route is measured so the pointer never crosses text. The steps are an ordered list, so their
// order never depends on the pointer or the numbers.
const SendSteps: React.FC = () => {
  const { t } = useTranslation();
  const { containerRef, journey, live } = useJourneyPath();
  // "Your bank → LulPay → Mobile money or cash", one pill per part (each locale keeps the arrows).
  const pathParts = t('sendSteps.path')
    .split('→')
    .map((part) => part.trim())
    .filter(Boolean);

  const classes = ['send-steps'];
  if (journey) classes.push(`send-steps--${journey.mode}`);
  if (journey && live) classes.push('send-steps--live');

  return (
    <Section id="how-it-works" surface="paper">
      <SectionHeading eyebrow={t('sendSteps.eyebrow')} title={t('sendSteps.title')} subtitle={t('sendSteps.subtitle')} />

      <div ref={containerRef} className={classes.join(' ')}>
        {journey ? (
          <span
            className="send-steps__pointer"
            aria-hidden="true"
            style={
              {
                offsetPath: `path('${journey.travel}')`,
                // Where the route leaves badges 1–4 (--l1…--l4) and reaches badges 2–5 (--a2…--a5).
                ...Object.fromEntries([
                  ...journey.leave.map((value, i) => [`--l${i + 1}`, `${(value * 100).toFixed(2)}%`]),
                  ...journey.arrive.map((value, i) => [`--a${i + 2}`, `${(value * 100).toFixed(2)}%`]),
                ]),
              } as React.CSSProperties
            }
          />
        ) : null}

        <ol className="send-steps__grid">
          {STEPS.map((step) => (
            <Card key={step.n} as="li" icon={step.icon} className={`send-step ${columnClass(step.n)}`}>
              <span className="send-step__number" aria-hidden="true">
                {step.n}
              </span>
              <h3 className="send-step__title">{t(step.titleKey)}</h3>
              <p className="send-step__body">{t(step.bodyKey)}</p>
            </Card>
          ))}
        </ol>
      </div>

      <ol className="send-path" aria-label={t('sendSteps.path')}>
        {pathParts.map((part, index) => {
          const pillClass = ['send-path__pill'];
          if (index === 1) pillClass.push('send-path__pill--anchor');
          if (index === pathParts.length - 1) pillClass.push('send-path__pill--payout');
          return (
            <li key={part} className="send-path__step">
              {index > 0 ? (
                <span className="send-path__arrow" aria-hidden="true">
                  <Icon name="arrow-right" size={20} />
                </span>
              ) : null}
              <span className={pillClass.join(' ')}>
                {/* Where "your bank" is: the flags of the countries you can send from (the caption says it in words). */}
                {index === 0 ? (
                  <span className="send-path__flags" aria-hidden="true">
                    {CORRIDORS.map((corridor) => (
                      <img key={corridor.id} src={corridor.flag} alt="" width={20} height={20} />
                    ))}
                  </span>
                ) : null}
                {/* Where it lands: African payout countries, Uganda first and in front. */}
                {index === pathParts.length - 1 ? (
                  <span className="send-path__flags send-path__flags--payout" aria-hidden="true">
                    {PAYOUT_FLAGS.map((country) => (
                      <img key={country.id} src={country.flag} alt="" width={20} height={20} />
                    ))}
                  </span>
                ) : null}
                {part}
              </span>
            </li>
          );
        })}
      </ol>
      <p className="send-path__note">{t('sendSteps.pathNote')}</p>
    </Section>
  );
};

export default SendSteps;
